import {
  Mail, Calendar, CalendarDays, BookOpen, FileCheck, Folder, Contact, Clock, FileText, Target,
  type LucideIcon,
} from '../icons';

/**
 * 도메인 칩과 시작 질문 — **한 곳**에 둔다 (설계 판 B·D · 정의서 C7·C8).
 *
 * ★★★ **판 D 에서 셋 → 아홉이 됐다.** 판 B 때는 도구가 넷(calendar·chat·hr·minutes)뿐이었고
 *    그중 `chat.*` 는 방 id 가 있어야 돌아 패널에서 못 써서 **셋**만 세웠다. 판 D 가
 *    메일 · 게시판 · 예약 · 전자결재 · 주소록 · 문서의 **읽기 도구**를 붙이면서 나머지가 섰다.
 * ★★ 정의서 C7 의 일곱(메일·캘린더·예약·게시판·전자결재·문서·주소록)에 **근태·휴가**와
 *    **AI 회의록**을 더한 아홉이다 — 그 둘도 도구가 실재하는데 목록에 없으면 못 쓰는 것과 같다.
 *
 * ★ 시작 질문은 **지금 있는 도구가 실제로 답할 수 있는 것**만 골랐다. 정의서가 예시로 적어 둔
 *   「회신 필요로 분류된 메일」·「최근 열람한 문서」는 **제품에 그 분류·그 이력이 없어서** 뺐다
 *   (설계 §00-① 이 답할 수 없는 스타터로 지목한 둘이다).
 * ★ **쓰기는 아직 없다** — 「메일 보내줘」·「예약 잡아줘」는 확인 카드가 선 뒤에 연다.
 *   그래서 시작 질문도 전부 «묻는» 말이다.
 *
 * ⚠ 칩을 늘릴 때는 **도구가 먼저다.** 도구 없는 칩은 누를 때마다 「그건 못 합니다」가 온다 —
 *   `AiComposer.test.tsx` 가 orchestrator 카탈로그와 대조해 그것을 붙잡는다.
 * ⚠ 판 E 가 오면 이 목록은 `ai_agent_starters` 표로 옮겨 간다(정의서 D4·A9).
 */

export interface AiStarterDomain {
  key: string;
  label: string;
  icon: LucideIcon;
  /** 누르면 위로 열리는 목록. 고르면 입력창에 **글자로 꽂힌다**(바로 안 보낸다 — C8). */
  starters: readonly string[];
}

export const AI_STARTER_DOMAINS: readonly AiStarterDomain[] = [
  {
    key: 'mail',
    label: '메일',
    icon: Mail,
    starters: ['안 읽은 메일 있어?', '오늘 받은 메일을 정리해줘', '계약서 관련 메일 찾아줘'],
  },
  {
    key: 'calendar',
    label: '캘린더',
    icon: Calendar,
    starters: ['이번 주 내 일정 알려줘', '내일 비어 있는 시간 찾아줘', '다음 주 회의 일정을 정리해줘'],
  },
  {
    key: 'reservation',
    label: '예약',
    icon: CalendarDays,
    starters: ['내 예약 뭐 있지?', '예약할 수 있는 회의실 알려줘'],
  },
  {
    key: 'board',
    label: '게시판',
    icon: BookOpen,
    starters: ['최근 공지 뭐 올라왔어?', '휴가 관련 게시글 찾아줘'],
  },
  {
    key: 'approval',
    label: '전자결재',
    icon: FileCheck,
    starters: ['내가 결재할 문서 있어?', '내가 올린 결재 진행 상황 알려줘', '완료된 결재 문서 보여줘'],
  },
  {
    key: 'workspace',
    label: '문서',
    icon: Folder,
    starters: ['보고서 문서 찾아줘', '지난달 자료 어디 있지?'],
  },
  {
    key: 'contacts',
    label: '명함첩',
    icon: Contact,
    starters: ['연락처 찾아줘', '이 사람 메일 주소 알려줘'],
  },
  {
    key: 'hr',
    label: '근태 · 휴가',
    icon: Clock,
    starters: ['내 연차 며칠 남았어?', '이번 달 내 근태를 요약해줘', '내 사원 정보 알려줘'],
  },
  {
    key: 'minutes',
    label: 'AI 회의록',
    icon: FileText,
    starters: ['최근 회의록 목록 보여줘', '지난 회의 내용을 요약해줘'],
  },
  {
    /*
     * 판 X3 — 열째. **새 것은 끝에 붙인다**(앞 차례를 흔들지 않는다).
     * ★ 아이콘은 `Target` — 이 제품이 이미 과제 알림에 쓰는 것이다(`notification-meta.ts`).
     *   AI 만 다른 그림을 쓰면 같은 것이 두 얼굴이 된다.
     * ★ 시작 질문은 **지금 도구가 실제로 답할 수 있는 것**만이다 — 마일스톤은 표가 없어서 뺐다.
     */
    key: 'project',
    label: '프로젝트',
    icon: Target,
    starters: ['내 프로젝트 진행 상황 알려줘', '기한 지난 내 과제 있어?', '내 할 일 뭐 남았어?'],
  },
];
